import { Injectable } from "@angular/core"
import { EMPTY, Observable, expand, map, reduce } from "rxjs"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { Recording, RecordingStationMessage } from "../models/recording.model"

/** Body text from a recording API error. Backend sends `{ error, message }` with the same guidance. */
export function recordingApiError(err: unknown, fallback: string): string {
  const body = (err as { error?: unknown } | null)?.error
  if (typeof body === "string" && body.trim()) return body.trim()
  if (body && typeof body === "object") {
    const record = body as { error?: unknown; message?: unknown }
    for (const value of [record.error, record.message]) {
      if (typeof value === "string" && value.trim()) return value.trim()
    }
  }
  return fallback
}

/**
 * API response from GET /audioRecordings
 */
interface ListResponse {
  status: string
  records: Recording[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
}

/**
 * API response from POST /audioRecordings
 */
interface CreateResponse {
  status: string
  recordingId: number
  audio_url: string
  message: string
  autoApproved?: boolean
  approvalStatus?: number
}

@Injectable({ providedIn: "root" })
export class RecordingService {
  private baseUrl = `${environment.apiUrl}/audioRecordings`

  constructor(private api: ApiService) { }

  // =========================
  // LIST RECORDINGS
  // =========================
  list(params?: {
    status?: number
    talentid?: number
    producttype?: number
    name?: string
    station_id?: number
    owner_id?: number
    limit?: number
    offset?: number
    sort_by?: string
    sort_order?: "ASC" | "DESC"
    length?: number
    preemptible_only?: boolean
  }): Observable<ListResponse> {
    const query = new URLSearchParams()

    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        query.set(k, String(v))
      }
    })

    const url = query.toString()
      ? `${this.baseUrl}?${query.toString()}`
      : this.baseUrl

    return this.api.authenticatedGet<ListResponse>(url).pipe(
      map((res) => ({
        ...res,
        records: (res.records || []).map((r) => ({
          ...r,
          presigned_url: r.audio_url
        }))
      }))
    )
  }

  /**
   * Every matching recording (walks pages of 100 — get_audio_recordings defaults to 50 and
   * caps at 100, so a single list() call silently truncated dropdowns).
   */
  listAll(params?: Omit<NonNullable<Parameters<RecordingService["list"]>[0]>, "limit" | "offset">): Observable<ListResponse> {
    const limit = 100
    return this.list({ ...params, limit, offset: 0 }).pipe(
      expand((res) =>
        res.pagination?.has_more && (res.records?.length ?? 0) > 0
          ? this.list({ ...params, limit, offset: (res.pagination.offset ?? 0) + (res.pagination.limit || limit) })
          : EMPTY,
      ),
      reduce(
        (acc: ListResponse | null, res: ListResponse) =>
          acc ? { ...acc, records: acc.records.concat(res.records || []) } : { ...res, records: [...(res.records || [])] },
        null,
      ),
      map((all) => {
        const records = all?.records ?? []
        return {
          status: all?.status ?? "success",
          records,
          pagination: { total: records.length, limit: records.length, offset: 0, has_more: false },
        }
      }),
    )
  }

  // =========================
  // CREATE RECORDING
  // =========================
  /**
   * Backend decides uploader from JWT
   * status is NOT sent (station approvals handle it)
   *
   * When `suppress_customer_notifications` is true, the API should not create in-app (or other)
   * notifications aimed at advertiser `user` accounts — used when admin / owner / station roles upload.
   */
  create(payload: {
    name: string
    uploaderid: number
    status: number
    talentid: number
    producttype: number
    audio_tmp_key: string
    audiolength: number
    submitted_stations?: number[]
    product_type_ids?: number[]
    suppress_customer_notifications?: boolean
    is_preemptible_promo?: boolean
  }): Observable<any> {
    return this.api.authenticatedPost(this.baseUrl, payload)
  }

  // =========================
  // APPROVE / DECLINE
  // =========================
  /**
   * Station-level approval
   * Admin can approve any station
   * Owner / station_owner only their stations
   */
  approve(recordingId: number, stationId: number, productTypeIds: number[] = []): Observable<any> {
    return this.api.authenticatedPut(
      `${this.baseUrl}/${recordingId}`,
      {
        action: "approve",
        station_id: stationId,
        product_type_ids: productTypeIds,
      }
    )
  }

  decline(recordingId: number, stationId: number, reason: string): Observable<any> {
    return this.api.authenticatedPut(
      `${this.baseUrl}/${recordingId}`,
      {
        action: "decline",
        station_id: stationId,
        reason,
      }
    )
  }

  /**
   * Uploader submits a new audio file for one declined station.
   * Creates a new recording (pending, or auto-approved for station staff)
   * and marks this station's declined row superseded. The message thread
   * is copied onto the new recording id.
   */
  resubmitNew(
    recordingId: number,
    stationId: number,
    audioTmpKey: string,
    audiolength: number,
  ): Observable<{
    status: string
    message: string
    recording_id: number
    resubmitted_from_recording_id: number
    station_id: number
    approval_status: number
    autoApproved: boolean
    audio_url?: string
  }> {
    return this.api.authenticatedPut(`${this.baseUrl}/${recordingId}`, {
      action: "resubmit_new",
      station_id: stationId,
      audio_tmp_key: audioTmpKey,
      audiolength,
    })
  }

  listMessages(recordingId: number, stationId: number): Observable<{
    status: string
    recording_id: number
    station_id: number
    messages: RecordingStationMessage[]
  }> {
    return this.api.authenticatedGet(
      `${this.baseUrl}/${recordingId}/messages?station_id=${stationId}`,
    )
  }

  postMessage(recordingId: number, stationId: number, messageText: string): Observable<{
    status: string
    message: RecordingStationMessage
  }> {
    return this.api.authenticatedPost(`${this.baseUrl}/${recordingId}/messages`, {
      station_id: stationId,
      message_text: messageText,
    })
  }

  // =========================
  // UPDATE METADATA / AUDIO
  // =========================
  update(
    id: number,
    payload: {
      name?: string
      talentid?: number
      talent_name?: string
      audio_tmp_key?: string
      audiolength?: number
      product_type_ids?: number[]
      is_preemptible_promo?: boolean
      station_id?: number
    },
  ): Observable<any> {
    return this.api.authenticatedPut(`${this.baseUrl}/${id}`, {
      action: "update",
      ...payload,
    })
  }

  // =========================
  // DELETE
  // =========================
  delete(id: number): Observable<any> {
    return this.api.authenticatedDelete(`${this.baseUrl}/${id}`)
  }
}
